// Fee display and the worked examples on the Fees tab.
//
// On the wire a flat fee is integer CENTS and a percentage is 0–100. The UI
// only ever shows and accepts dollars and percents.
//
// The examples mirror the backend (illustrative; the server is the
// authority for real charges):
//   ticketing-fee.ts     computeFee: % of the order subtotal (half-up), or a
//                        flat fee per order capped at the subtotal. The
//                        league absorbs it, plus Stripe's cut.
//   registration-fee.ts  quoteRegistration: "passed_on" grosses the total up
//                        so the league nets the face price,
//                        T = ceil((P + A + f) / (1 − s)); "absorbed" charges
//                        the sticker price and both cuts come off the league.
// Stripe's cut is the standard US card estimate, 2.9% + 30¢
// (DEFAULT_STRIPE_PERCENT / DEFAULT_STRIPE_FIXED_CENTS).

import type { FeeSetting, FeeType, RegistrationFeeMode } from '@/api/types'

export const STRIPE_PERCENT = 2.9
export const STRIPE_FIXED_CENTS = 30
export const EXAMPLE_TICKET_ORDER_CENTS = 5000
export const EXAMPLE_REGISTRATION_CENTS = 5000

/** 1234 → "$12.34" */
export function usd(cents: number): string {
  const sign = cents < 0 ? '−' : ''
  return `${sign}$${(Math.abs(cents) / 100).toFixed(2)}`
}

/** "7.5%" / "$1.50 per order" */
export function feeLabel(f: Pick<FeeSetting, 'type' | 'value'>, per = 'per order'): string {
  if (f.type === 'percentage') return `${formatPercent(f.value)}%`
  return `${usd(f.value)} ${per}`
}

export function formatPercent(n: number): string {
  return Number.isInteger(n) ? String(n) : String(Number(n.toFixed(2)))
}

/** Dollars typed by a person → integer cents, or null if it isn't a valid amount. */
export function dollarsToCents(input: string): number | null {
  const s = input.trim().replace(/^\$/, '').replace(/,/g, '')
  if (!/^\d+(\.\d{0,2})?$|^\.\d{1,2}$/.test(s)) return null
  return Math.round(Number(s) * 100)
}

/** Cents → the string the dollar input shows ("1.50"). */
export function centsToDollars(cents: number): string {
  return (cents / 100).toFixed(2)
}

/** Percent typed by a person → number 0–100 (≤ 2 decimals), or null. */
export function parsePercent(input: string): number | null {
  const s = input.trim().replace(/%$/, '')
  if (!/^\d+(\.\d{0,2})?$|^\.\d{1,2}$/.test(s)) return null
  const n = Number(s)
  return n >= 0 && n <= 100 ? n : null
}

export function marginCents(subtotalCents: number, fee: { type: FeeType; value: number }): number {
  if (subtotalCents <= 0) return 0
  if (fee.type === 'flat') return Math.min(Math.max(0, Math.round(fee.value)), subtotalCents)
  return Math.max(0, Math.round((subtotalCents * fee.value) / 100))
}

export function stripeCutCents(totalCents: number): number {
  return Math.round((totalCents * STRIPE_PERCENT) / 100) + STRIPE_FIXED_CENTS
}

export interface FeeExample {
  priceCents: number
  fanPays: number
  stripe: number
  platform: number
  leagueNets: number
}

/** A ticket order: the fan pays the subtotal; Stripe and our fee come off the league. */
export function ticketingExample(fee: { type: FeeType; value: number }, subtotal = EXAMPLE_TICKET_ORDER_CENTS): FeeExample {
  const platform = marginCents(subtotal, fee)
  const stripe = stripeCutCents(subtotal)
  return { priceCents: subtotal, fanPays: subtotal, stripe, platform, leagueNets: Math.max(0, subtotal - stripe - platform) }
}

/** A paid registration under either mode. */
export function registrationExample(
  fee: { type: FeeType; value: number },
  mode: RegistrationFeeMode,
  price = EXAMPLE_REGISTRATION_CENTS,
): FeeExample {
  const platform = marginCents(price, fee)
  const total = mode === 'absorbed' ? price : Math.ceil((price + platform + STRIPE_FIXED_CENTS) / (1 - STRIPE_PERCENT / 100))
  const stripe = stripeCutCents(total)
  return { priceCents: price, fanPays: total, stripe, platform, leagueNets: Math.max(0, total - stripe - platform) }
}
